import { useSelector } from 'react-redux';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { getImageBaseUrl } from 'helpers/request';
import {
  bikeColourSelector,
  bikeModelSelector,
  getPack,
  bikeSummaryAccessoriesSelector,
  getPackAndAccessoriesTotal,
  getPackSelectedTotal,
  getSelectedWarrantyPack,
  getSelectedServicePack,
  getPackFittingTimeTotal,
} from 'src/redux/bike/selectors';
import { accessoryExtrasSelector, getSelectedProvince } from 'src/redux/configurator/selectors';
import {
  labelsSelector,
  disclaimerSelector,
  pdfLogoSelector,
  getProvincePricingEnabled,
} from 'src/redux/app/selectors';
import { DIN2014 } from 'components/_utility/Fonts/DIN2014';
import { DIN2014Regular } from 'components/_utility/Fonts/DIN2014-Regular';
import { NotoSansJapanese } from 'components/_utility/Fonts/NotoSansJapanese-Medium';
import { NotoSerifThaiMedium } from 'components/_utility/Fonts/NotoSerifThai-Medium';
import { formatFittingTime, isIE, isEdge, getLanguageCode } from 'helpers/utilities';
import usePrice from 'src/hooks/usePrice';
import useProvincialFees from 'src/hooks/useProvincialFees';
import cloudinary from 'helpers/cloudinary';

function setFontBasedOnLanguage(doc, languageCode, dinFontVariant = 'DIN2014Regular') {
  switch (languageCode) {
    case 'ja-jp':
      doc.addFileToVFS('NotoSansJapanese-Medium.ttf', NotoSansJapanese);
      doc.addFont('NotoSansJapanese-Medium.ttf', 'NotoSansJapanese', 'normal');
      doc.setFont('NotoSansJapanese');
      return 'NotoSansJapanese';

    case 'th-th':
      doc.addFileToVFS('NotoSerifThai-Medium.ttf', NotoSerifThaiMedium);
      doc.addFont('NotoSerifThai-Medium.ttf', 'NotoSerifThaiMedium', 'normal');
      doc.setFont('NotoSerifThaiMedium');
      return 'NotoSerifThaiMedium';

    default:
      doc.addFileToVFS('DIN2014-Demi.ttf', DIN2014);
      doc.addFont('DIN2014-Demi.ttf', 'DIN2014', 'normal');

      doc.addFileToVFS('DIN2014-Regular.ttf', DIN2014Regular);
      doc.addFont('DIN2014-Regular.ttf', 'DIN2014Regular', 'normal');
      doc.setFont('DIN2014Regular'); // Default font
      return dinFontVariant === 'DIN2014Regular' ? 'DIN2014Regular' : 'DIN2014';
  }
}

export const useGeneratePDF = () => {
  const bikeModel = useSelector(bikeModelSelector);
  const bikeColour = useSelector(bikeColourSelector);
  const bikeAccessories = useSelector(bikeSummaryAccessoriesSelector);
  const formatPrice = usePrice();
  const label = useSelector(labelsSelector);
  const disclaimers = useSelector(disclaimerSelector);
  const extras = useSelector(accessoryExtrasSelector);
  const { fees } = extras || {};
  const feesTotal = extras?.fees.reduce((prevTotal, fee) => prevTotal + Number(fee?.price || 0), 0);
  const provincePricingEnabled = useSelector(getProvincePricingEnabled);
  const selectedProvince = useSelector(getSelectedProvince);
  const { fees: provincialFees } = useProvincialFees(selectedProvince?.id, bikeModel);

  let provincialFeesTotal = 0;
  if (provincePricingEnabled && selectedProvince && provincialFees) {
    provincialFeesTotal = (provincialFees.dealerFee || 0) + (provincialFees.freightFee || 0);
  }
  const totalFees = provincePricingEnabled && selectedProvince ? provincialFeesTotal : feesTotal;
  const basicPriceWithoutFees = bikeModel.price + (bikeColour.price - bikeModel.price);
  const basicPrice = basicPriceWithoutFees + totalFees;
  const pdfLogo = useSelector(pdfLogoSelector);
  const packs = useSelector(getPack);
  const selectedWarrantyPack = useSelector(getSelectedWarrantyPack);
  const selectedServicePack = useSelector(getSelectedServicePack);
  const luxuryTax = disclaimers.showLuxuryTaxDisclaimers;
  const packAndAccessoriesTotal = useSelector(getPackAndAccessoriesTotal);
  const packSelectedTotal = useSelector(getPackSelectedTotal);
  const packFittingTimeTotal = useSelector(getPackFittingTimeTotal);
  const languageCode = getLanguageCode().toLowerCase();

  const generatePDF = async () => {
    const getImages = async () => {
      const convertToPngDataUrl = dataUri =>
        new Promise((resolve, reject) => {
          const image = new Image();

          image.onload = function() {
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0);

            const pngDataUrl = canvas.toDataURL('image/png');
            resolve(pngDataUrl);
          };

          image.onerror = function() {
            reject(new Error('Failed to load the provided image data URI.'));
          };

          image.src = dataUri;
        });

      const bikeColourPromise = new Promise(async resolve => {
        let bikeColourDataUrl = null;

        if (bikeColour && bikeColour.swatchImage) {
          const response = await fetch(cloudinary(bikeColour.swatchImage, 'c_thumb,w_350'));
          const responseBlob = await response.blob();

          bikeColourDataUrl = await new Promise((resolveDataUrl, rejectDataUrl) => {
            const reader = new FileReader();
            reader.onloadend = () => resolveDataUrl(reader.result);
            reader.onerror = rejectDataUrl;
            reader.readAsDataURL(responseBlob);
          });

          bikeColourDataUrl = await convertToPngDataUrl(bikeColourDataUrl);
        }

        resolve({
          ...bikeColour,
          imageData: bikeColourDataUrl,
        });
      });

      const bikeModelPromise = new Promise(async resolve => {
        let dataUrl = null;
        if (bikeModel.thumbnail && bikeModel.thumbnail.src) {
          const response = await fetch(cloudinary(bikeModel.thumbnail.src, 'c_thumb,w_350'));
          const responseBlob = await response.blob();

          dataUrl = await new Promise((resolveDataUrl, rejectDataUrl) => {
            const reader = new FileReader();
            reader.onloadend = () => resolveDataUrl(reader.result);
            reader.onerror = rejectDataUrl;
            reader.readAsDataURL(responseBlob);
          });
          dataUrl = await convertToPngDataUrl(dataUrl);
        }

        resolve({
          ...bikeModel,
          imageData: dataUrl,
        });
      });

      const pdfLogoPromise = new Promise(async resolve => {
        let dataUrl = null;
        if (pdfLogo && pdfLogo.src) {
          const response = await fetch(getImageBaseUrl() + pdfLogo.src);
          const responseBlob = await response.blob();

          dataUrl = await new Promise((resolveDataUrl, rejectDataUrl) => {
            const reader = new FileReader();
            reader.onloadend = () => resolveDataUrl(reader.result);
            reader.onerror = rejectDataUrl;
            reader.readAsDataURL(responseBlob);
          });

          dataUrl = await convertToPngDataUrl(dataUrl);
        }

        resolve({
          ...pdfLogo,
          imageData: dataUrl,
        });
      });

      const accessoryImagePromises = bikeAccessories.map(
        accessory =>
          new Promise(async resolve => {
            if (!accessory.images.length) {
              return resolve({
                ...accessory,
                // Triumph Triangle Base64
                imageData:
                  'data:image/png;base64,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',
              });
            }

            const response = await fetch(cloudinary(accessory.images[0].src, 'c_thumb,w_100'));
            const responseBlob = await response.blob();

            let dataUrl = await new Promise((resolveDataUrl, rejectDataUrl) => {
              const reader = new FileReader();
              reader.onloadend = () => resolveDataUrl(reader.result);
              reader.onerror = rejectDataUrl;
              reader.readAsDataURL(responseBlob);
            });
            dataUrl = await convertToPngDataUrl(dataUrl);
            resolve({
              ...accessory,
              imageData: dataUrl,
            });
          })
      );

      const bikePackPromise = new Promise(async resolve => {
        let bikePackDataUrl = null;

        if (packs && packs?.thumbnail?.src) {
          const response = await fetch(cloudinary(packs.thumbnail.src, 'c_thumb,w_80'));
          const responseBlob = await response.blob();

          bikePackDataUrl = await new Promise((resolveDataUrl, rejectDataUrl) => {
            const reader = new FileReader();
            reader.onloadend = () => resolveDataUrl(reader.result);
            reader.onerror = rejectDataUrl;
            reader.readAsDataURL(responseBlob);
          });

          bikePackDataUrl = await convertToPngDataUrl(bikePackDataUrl);
        }

        resolve({
          ...packs,
          imageData: bikePackDataUrl,
        });
      });

      const [
        _bikeColourWithImage,
        _bikeModelWithImage,
        _pdfLogoWithImage,
        _bikePackWithImage,
        ..._bikeAccessoriesWithImages
      ] = await Promise.all([
        bikeColourPromise,
        bikeModelPromise,
        pdfLogoPromise,
        bikePackPromise,
        ...accessoryImagePromises,
      ]);

      return {
        bikeColourWithImage: _bikeColourWithImage,
        bikeModelWithImage: _bikeModelWithImage,
        pdfLogoWithImage: _pdfLogoWithImage,
        bikePackWithImage: _bikePackWithImage,
        bikeAccessoriesWithImages: _bikeAccessoriesWithImages,
      };
    };

    const {
      bikeColourWithImage,
      bikeModelWithImage,
      pdfLogoWithImage,
      bikePackWithImage,
      bikeAccessoriesWithImages,
    } = await getImages();

    const accessoryData = {
      head: [
        [
          '',
          (label.nameLabel || '').toUpperCase(),
          (label.priceLabel || '').toUpperCase(),
          (label.fittingTimeLabel || '').toUpperCase(),
          (label.quantityLabel || '').toUpperCase(),
        ],
      ],
      body: [],
    };

    const packsData = {
      head: [
        [
          '',
          (label.nameLabel || '').toUpperCase(),
          (label.priceLabel || '').toUpperCase(),
          (label.fittingTimeLabel || '').toUpperCase(),
          '',
        ],
      ],
      body: [],
    };

    const accessoriesArray = [];
    const accessoryImages = [];
    const packsArray = [];

    if (bikeAccessoriesWithImages.length) {
      bikeAccessoriesWithImages.map(accessory => {
        accessoryImages.push(accessory.imageData);
        return accessoriesArray.push([
          '',
          `${accessory.name.toUpperCase()} \n\n${(
            label.partNumberLabel || ''
          ).toUpperCase()}: ${accessory.partNumber.toUpperCase()} `,
          accessory.price ? formatPrice(accessory.price) : accessory.priceLocalised,
          formatFittingTime(accessory.fittingTime),
          accessory.quantity,
        ]);
      });
    }

    if (packs) {
      packsArray.push([
        '',
        `${packs.name || ''}`,
        `${packs ? formatPrice(packSelectedTotal) : ''}`,
        `${packs ? formatFittingTime(packFittingTimeTotal) : ''}`,
        '',
      ]);
    }

    if (luxuryTax && bikeAccessoriesWithImages.length) {
      accessoriesArray.push([
        `${label.packAndAccessoriesLabel || ''}\n${formatPrice(packAndAccessoriesTotal)}`,
        '',
        '',
        '',
        '',
      ]);
    }

    if (luxuryTax && packs && !bikeAccessoriesWithImages.length) {
      packsArray.push([
        `${label.packAndAccessoriesLabel || ''}\n${formatPrice(packAndAccessoriesTotal)}`,
        '',
        '',
        '',
        '',
      ]);
    }

    const formatPriceDisclaimer = disclaimers.price ? disclaimers.price.replace(/<br\s*\/?>/gi, '\n') : '';
    const formatFinanceDisclaimer = disclaimers.finance ? disclaimers.finance.replace(/<br\s*\/?>/gi, '\n') : '';
    const formatBasicPriceDisclaimer = disclaimers.basicPrice
      ? disclaimers.basicPrice.replace(/<br\s*\/?>/gi, '\n')
      : '';
    const formatAccessoriesPriceDisclaimer = disclaimers.accessoriesPrice
      ? disclaimers.accessoriesPrice.replace(/<br\s*\/?>/gi, '\n')
      : '';
    const formatPDFDisclaimer = disclaimers.pdf ? disclaimers.pdf.replace(/<br\s*\/?>/gi, '\n') : '';
    const formatPacksDisclaimer = disclaimers.packTotal ? disclaimers.packTotal.replace(/<br\s*\/?>/gi, '\n') : '';

    const doc = new JsPDF({
      unit: 'px',
      filters: ['ASCIIHexEncode'],
      orientation: 'p',
      format: 'a4',
    });

    doc.setFontSize(25);
    doc.setTextColor('black');
    setFontBasedOnLanguage(doc, languageCode);

    if (pdfLogoWithImage.imageData) {
      doc.addImage(pdfLogoWithImage.imageData, 'PNG', 305, 15, 125, 30);
    }

    doc.text(19, 47, (label.bikeSummaryLabel || '').toUpperCase());

    doc.setFontSize(16);
    doc.setTextColor('black');

    doc.setDrawColor(167, 169, 172);
    doc.setLineWidth(0.1);

    doc.text(19, 72, (label.bikeLabel || '').toUpperCase());

    doc.line(425, 76, 19, 76); // horizontal line

    if (bikeModelWithImage.imageData) {
      doc.addImage(bikeModelWithImage.imageData, 'PNG', 10, 77, 80, 54);
    }

    doc.setFontSize(16);
    doc.setTextColor('black');
    setFontBasedOnLanguage(doc, languageCode);

    const bikeTitle = doc.splitTextToSize(bikeModel.name.toUpperCase().replace(/(<([^>]+)>)/gi, ''), 180);
    doc.text(95, 110, bikeTitle);

    doc.setTextColor('#585a5f');
    doc.text(425, 110, formatPrice(bikeModel.price), 'right');

    doc.setTextColor('black');
    doc.text(19, 172, (label.colourLabel || '').toUpperCase());

    doc.line(425, 176, 19, 176);

    if (bikeColourWithImage.imageData) {
      doc.addImage(bikeColourWithImage.imageData, 'JPEG', 19, 190, 60, 46);
    }

    const colourTitle = doc.splitTextToSize(bikeColour.name.toUpperCase(), 180);

    doc.text(95, 200, colourTitle);

    doc.setFontSize(16);
    doc.setTextColor('#585a5f');
    if (bikeColour.price === bikeModel.price) {
      doc.text(425, 200, label.includedAsStandardLabel || '', 'right');
    } else {
      doc.text(425, 200, formatPrice(bikeColour.price - bikeModel.price), 'right');
    }

    doc.setTextColor('black');
    doc.setDrawColor(167, 169, 172);

    // Dynamic positioning to reduce dead space after color section
    const priceStartY = !bikeAccessoriesWithImages.length && !packs ? 250 : 265;
    let priceEndY = 0; // Track where the price section ends
    if (luxuryTax) {
      if (!bikeAccessoriesWithImages.length && !packs) {
        doc.text(425, priceStartY, label.totalSellingPriceLabel || '', 'right');
      }
      if (bikeAccessoriesWithImages.length || packs) {
        doc.text(425, priceStartY, label.basicPriceLabel || '', 'right');
      }

      doc.text(425, priceStartY + 15, formatPrice(basicPrice), 'right');
      priceEndY = priceStartY + 15;
    }
    if (!luxuryTax && !bikeAccessoriesWithImages.length && !packs) {
      if (provincePricingEnabled && selectedProvince) {
        doc.text(425, priceStartY, label.msrpLabel || '', 'right');
        doc.text(425, priceStartY + 15, formatPrice(basicPriceWithoutFees), 'right');
      } else {
        doc.text(425, priceStartY, label.totalSellingPriceLabel || '', 'right');
        doc.text(425, priceStartY + 15, formatPrice(basicPrice), 'right');
      }
      priceEndY = priceStartY + 15;
    }

    // Dynamic positioning for packs/accessories sections
    const basePacksY = priceStartY + 45; // Start packs section 45px after price section
    // Check if only a pack is selected without additional accessories
    if (packs && !bikeAccessoriesWithImages.length && !selectedServicePack && !selectedWarrantyPack && !fees) {
      if (luxuryTax) {
        doc.text(19, basePacksY + 50, (label.packsLabel || '').toUpperCase());
        doc.line(425, basePacksY + 54, 19, basePacksY + 54); // horizontal line
      } else {
        doc.text(19, basePacksY, (label.packsLabel || '').toUpperCase());
        doc.line(425, basePacksY + 3, 19, basePacksY + 3); // horizontal line
      }
    }

    // Check for packs and additional accessories <------- continue with moving accessories down and packs up (packs need to go first)
    if (packs && bikeAccessoriesWithImages.length && !selectedServicePack && !selectedWarrantyPack && !fees) {
      if (luxuryTax) {
        doc.text(19, basePacksY + 50, (label.packsLabel || '').toUpperCase());
        doc.line(425, basePacksY + 54, 19, basePacksY + 54); // horizontal line
        doc.text(19, basePacksY + 165, (label.accessoriesLabel || '').toUpperCase());
        doc.line(425, basePacksY + 168, 19, basePacksY + 168); // horizontal line
      } else {
        doc.text(19, basePacksY, (label.packsLabel || '').toUpperCase());
        doc.line(425, basePacksY + 3, 19, basePacksY + 3); // horizontal line
        doc.text(19, basePacksY + 110, (label.accessoriesLabel || '').toUpperCase());
        doc.line(425, basePacksY + 113, 19, basePacksY + 113); // horizontal line
      }
    }

    doc.setTextColor('#585a5f');
    doc.setFontSize(14);

    // y coordinate value for the rest of the file, this value will be updated checking on sections
    let startY = 297;

    // define and call the function at the same time
    const printTotalCareSection = (function() {
      if (selectedServicePack || selectedWarrantyPack) {
        setFontBasedOnLanguage(doc, languageCode);
        doc.setFontSize(16);
        doc.setTextColor('black');

        doc.setDrawColor(167, 169, 172);
        doc.setLineWidth(0.1);

        doc.text(19, startY, 'Total Care'.toUpperCase());
        doc.line(425, startY + 4, 19, startY + 4); // horizontal line
      }

      doc.setFontSize(14);

      startY = 319; // update the y coordinate value once the main title is written
      if (selectedWarrantyPack) {
        const printSelectedWarrantyPack = (function() {
          const selectedWarrantyDesWithoutHTMLTags = selectedWarrantyPack.description
            ? selectedWarrantyPack.description.replace(/<[^>]+>/gi, '')
            : '';
          const selectedWarrantyDesWithoutHTMLEntities = selectedWarrantyDesWithoutHTMLTags
            .replace(/&#8217;/gi, "'")
            .replace(/&#8211;/gi, '–')
            .replace(/&nbsp;/gi, ' ');
          const formattedSelectedWarrantyDes = `${selectedWarrantyDesWithoutHTMLEntities
            .split('.')
            .slice(0, -1)
            .join('.')}.`;
          doc.text(19, startY, selectedWarrantyPack.title);
          doc.setFontSize(12);
          doc.setTextColor('#585a5f');
          const splitDescription = doc.splitTextToSize(formattedSelectedWarrantyDes, 420);
          doc.text(19, startY + 20, splitDescription);
          doc.setTextColor('black');
        })();
      }

      startY = selectedWarrantyPack ? 430 : 319;
      if (selectedServicePack) {
        const printServicePack = (function() {
          doc.setFontSize(14);
          doc.setTextColor('black');
          doc.text(19, startY, selectedServicePack.title);
          const formatSelectedServicePackDescription = selectedServicePack.description
            ? selectedServicePack.description.replace(/<[^>]+>/g, '')
            : '';
          const splitDescription = doc.splitTextToSize(formatSelectedServicePackDescription, 480);
          doc.setTextColor('#585a5f');
          doc.setFontSize(12);
          doc.text(19, startY + 15, splitDescription);
          doc.setTextColor('black');
          setFontBasedOnLanguage(doc, languageCode);
        })();
      }
    })();

    if (selectedServicePack || selectedWarrantyPack) {
      let totalCarePriceForDisplay = 0;
      if (selectedWarrantyPack && selectedWarrantyPack.price) {
        const price =
          typeof selectedWarrantyPack.price === 'string'
            ? parseFloat(selectedWarrantyPack.price.replace(/[^0-9.-]/g, ''))
            : Number(selectedWarrantyPack.price);
        totalCarePriceForDisplay += price || 0;
      }
      if (selectedServicePack && selectedServicePack.price) {
        const price =
          typeof selectedServicePack.price === 'string'
            ? parseFloat(selectedServicePack.price.replace(/[^0-9.-]/g, ''))
            : Number(selectedServicePack.price);
        totalCarePriceForDisplay += price || 0;
      }

      let totalCareY;
      if (selectedWarrantyPack && selectedServicePack) {
        totalCareY = 500;
      } else if (selectedWarrantyPack) {
        totalCareY = 420;
      } else {
        totalCareY = 390;
      }

      doc.setFontSize(16);
      doc.setTextColor('black');
      setFontBasedOnLanguage(doc, languageCode, 'DIN2014');
      doc.text(425, totalCareY, 'Total care', 'right');
      doc.text(425, totalCareY + 15, formatPrice(totalCarePriceForDisplay), 'right');
    }

    if (selectedWarrantyPack && selectedServicePack) {
      startY = 520;
    } else if (selectedWarrantyPack) {
      startY = 440;
    } else if (selectedServicePack) {
      startY = 410;
    } else {
      startY = 335;
    }

    const hasTotalCare = selectedServicePack || selectedWarrantyPack;

    if (hasTotalCare) {
      doc.addPage();
      startY = 80;

      doc.setFontSize(16);
      doc.setTextColor('black');

      const packsTitleStartY = startY - 30;
      if (packs && bikeAccessoriesWithImages.length) {
        doc.text(
          19,
          packsTitleStartY,
          `${(label.packsLabel || '').toUpperCase()} & ${(label.accessoriesLabel || '').toUpperCase()}`
        );
        doc.line(425, packsTitleStartY + 4, 19, packsTitleStartY + 4); // horizontal line
      } else if (packs && !bikeAccessoriesWithImages.length) {
        doc.text(19, packsTitleStartY, (label.packsLabel || '').toUpperCase());
        doc.line(425, packsTitleStartY + 4, 19, packsTitleStartY + 4);
      } else if (bikeAccessoriesWithImages.length && !packs) {
        doc.text(19, packsTitleStartY, (label.accessoriesLabel || '').toUpperCase());
        doc.line(425, packsTitleStartY + 4, 19, packsTitleStartY + 4);
      }
    } else {
      doc.setFontSize(16);
      doc.setTextColor('black');

      const packsTitleStartY = startY;
      if (packs && bikeAccessoriesWithImages.length) {
        doc.text(
          19,
          packsTitleStartY,
          `${(label.packsLabel || '').toUpperCase()} & ${(label.accessoriesLabel || '').toUpperCase()}`
        );
        doc.line(425, packsTitleStartY + 4, 19, packsTitleStartY + 4);
      } else if (packs && !bikeAccessoriesWithImages.length) {
        doc.text(19, packsTitleStartY, (label.packsLabel || '').toUpperCase());
        doc.line(425, packsTitleStartY + 4, 19, packsTitleStartY + 4);
      } else if (bikeAccessoriesWithImages.length && !packs) {
        doc.text(19, packsTitleStartY, (label.accessoriesLabel || '').toUpperCase());
        doc.line(425, packsTitleStartY + 4, 19, packsTitleStartY + 4);
      }

      startY += 30;
    }

    doc.autoTable({
      head: packs ? packsData.head : null,
      body: packs ? packsArray : '',
      startY,
      margin: {
        bottom: 20,
        right: 0,
        left: 0,
        top: 0,
      },
      styles: {
        overflow: 'linebreak',
        cellPadding: 20,
        lineColor: [255, 255, 255],
        lineWidth: 0.1,
        halign: 'left',
        valign: 'middle',
        textColor: [88, 90, 95],
        font: setFontBasedOnLanguage(doc, languageCode, 'DIN2014Regular'),
      },
      bodyStyles: {
        textColor: [88, 90, 95],
        halign: 'center',
        valign: 'middle',
      },
      headStyles: {
        textColor: [0, 0, 0],
        cellPadding: 0,
        font: setFontBasedOnLanguage(doc, languageCode, 'DIN2014'),
        fontStyle: 'normal',
        valign: 'middle',
        halign: 'center',
      },
      tableWidth: 'wrap',
      textColor: [88, 90, 95],
      columnStyles: {
        0: {
          cellWidth: 75,
          halign: 'center',
          valign: 'middle',
        },
        1: {
          cellWidth: 135,
          halign: 'center',
          valign: 'middle',
          padding: {
            top: 20,
            right: 20,
            bottom: 20,
            left: 10,
          },
        },
        2: {
          halign: 'center',
          cellWidth: 75,
        },
        3: {
          halign: 'center',
          cellWidth: 70,
        },
        4: {
          halign: 'center',
          cellWidth: 85,
          padding: {
            top: 20,
            right: 0,
            bottom: 20,
            left: 20,
          },
        },
      },
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      theme: 'plain',
      didDrawCell(cellData) {
        if (cellData.column.index === 0 && cellData.cell.section === 'body') {
          if (cellData.row.index === 0 && bikePackWithImage.imageData) {
            doc.addImage(
              bikePackWithImage.imageData,
              bikePackWithImage.imageData.match(/[^:/]\w+(?=;|,)/)[0].toUpperCase(),
              cellData.cell.x + 15,
              cellData.cell.y + 5,
              60,
              50
            );
          }
        }
      },
      didParseCell(cellData) {
        if (
          luxuryTax &&
          !bikeAccessoriesWithImages.length &&
          packs &&
          cellData.row.index === cellData.table.body.length - 1
        ) {
          cellData.cell.colSpan = 5;
          cellData.cell.styles.halign = 'right';
          cellData.cell.styles.fillColor = [255, 255, 255];
          cellData.cell.styles.fontSize = 16;
          cellData.cell.styles.textColor = [0, 0, 0];
          cellData.cell.styles.font = setFontBasedOnLanguage(doc, languageCode, 'DIN2014Regular');
          cellData.cell.styles.cellPadding = 20;
          cellData.cell.styles.valign = 'top';
        }
      },
      willDrawCell(HookData) {
        if (HookData.section === 'head' && HookData.pageNumber > 1) {
          return false;
        }
      },
    });

    const startYVal = luxuryTax ? 60 : 45;

    doc.autoTable({
      head: accessoriesArray.length ? accessoryData.head : '',
      body: accessoriesArray,
      startY: packs ? doc.lastAutoTable.finalY + startYVal : startY,
      margin: {
        bottom: 20,
        right: 0,
        left: 0,
      },
      styles: {
        overflow: 'linebreak',
        cellPadding: 10,
        lineColor: [255, 255, 255],
        lineWidth: 0.1,
        halign: 'left',
        valign: 'middle',
        textColor: [88, 90, 95],
        font: setFontBasedOnLanguage(doc, languageCode, 'DIN2014Regular'),
      },
      bodyStyles: {
        textColor: [88, 90, 95],
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        textColor: [0, 0, 0],
        cellPadding: 0,
        font: setFontBasedOnLanguage(doc, languageCode, 'DIN2014'),
        fontStyle: 'normal',
        valign: 'middle',
        halign: 'center',
      },
      tableWidth: 'wrap',
      textColor: [88, 90, 95],
      columnStyles: {
        0: {
          cellWidth: 75,
          halign: 'center',
          valign: 'top',
        },
        1: {
          cellWidth: 140,
          halign: 'center',
          valign: 'middle',
        },
        2: {
          halign: 'center',
          cellWidth: 70,
        },
        3: {
          halign: 'center',
          cellWidth: 70,
        },
        4: {
          halign: 'center',
          cellWidth: 85,
        },
      },
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      theme: 'plain',
      didDrawCell(cellData) {
        if (cellData.column.index === 0 && cellData.cell.section === 'body') {
          accessoryImages.map((img, i) =>
            cellData.row.index === i
              ? doc.addImage(
                  img,
                  img.match(/[^:/]\w+(?=;|,)/)[0].toUpperCase(),
                  cellData.cell.x + 10,
                  cellData.cell.y,
                  60,
                  50
                )
              : null
          );
        }
      },
      didParseCell(cellData) {
        if (luxuryTax && bikeAccessoriesWithImages.length && cellData.row.index === cellData.table.body.length - 1) {
          cellData.cell.colSpan = 5;
          cellData.cell.styles.halign = 'right';
          cellData.cell.styles.fillColor = [255, 255, 255];
          cellData.cell.styles.fontSize = 16;
          cellData.cell.styles.textColor = [0, 0, 0];
          cellData.cell.styles.font = setFontBasedOnLanguage(doc, languageCode, 'DIN2014Regular');
          cellData.cell.styles.cellPadding = 20;
          cellData.cell.styles.valign = 'top';
        }
      },
      willDrawCell(HookData) {
        if (HookData.section === 'head' && HookData.pageNumber > 1) {
          return false;
        }
      },
    });

    let packsAccessoriesEndY = 0;
    if ((bikeAccessoriesWithImages.length || packs) && packAndAccessoriesTotal > 0) {
      const packsAccessoriesY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 30 : 300;
      doc.setFontSize(16);
      doc.setTextColor('black');
      setFontBasedOnLanguage(doc, languageCode, 'DIN2014');
      doc.text(425, packsAccessoriesY, label.packAndAccessoriesLabel || 'Total pack et accessoires', 'right');
      doc.text(425, packsAccessoriesY + 15, formatPrice(packAndAccessoriesTotal), 'right');
      packsAccessoriesEndY = packsAccessoriesY + 20; // Track where pack/accessories total ends with a bit of buffer
    }

    // Page management constants
    const PAGE_HEIGHT = 630; // A4 usable height in pixels (after margins)
    const BOTTOM_MARGIN = 40; // Minimum space at bottom of page
    const TOP_MARGIN = 80; // Starting position for new pages
    // Calculate current position for smart page break
    let currentY = 300; // Default position
    let didAddPageBreak = false;

    if (!hasTotalCare) {
      // Determine the current Y position based on what content we have
      if (packsAccessoriesEndY > 0) {
        currentY = packsAccessoriesEndY;
      } else if (doc.lastAutoTable) {
        currentY = doc.lastAutoTable.finalY;
      } else if (priceEndY > 0) {
        // When no accessories/packs, use where the price section actually ended
        currentY = priceEndY;
      } else {
        // Default fallback
        currentY = 300;
      }

      // Only add a page if close to the bottom
      if (currentY > PAGE_HEIGHT - 150) {
        doc.addPage();
        didAddPageBreak = true;
      }
    }

    let totalY = 0;
    // Fee section calculation
    (function() {
      if ((fees && fees.length > 0) || (provincePricingEnabled && selectedProvince && provincialFees)) {
        // Check if we're on a new page or continuing on the same page
        const didAddPage = didAddPageBreak;
        let feesStartY;
        if (hasTotalCare && doc.lastAutoTable) {
          const spacing = (bikeAccessoriesWithImages.length || packs) && packAndAccessoriesTotal > 0 ? 70 : 40;
          feesStartY = doc.lastAutoTable.finalY + spacing;
        } else if (didAddPage) {
          feesStartY = 80;
        } else if (packsAccessoriesEndY > 0) {
          // If we have pack/accessories total, start fees after that
          feesStartY = packsAccessoriesEndY + 60;
        } else if (doc.lastAutoTable) {
          // If we have a table but no pack/accessories total
          feesStartY = doc.lastAutoTable.finalY + 40;
        } else {
          // Use the actual current position (which now correctly reflects where content ended)
          feesStartY = currentY + 40;
        }

        setFontBasedOnLanguage(doc, languageCode);
        doc.setFontSize(16);
        doc.setTextColor('black');

        doc.setDrawColor(167, 169, 172);
        doc.setLineWidth(0.1);

        doc.text(19, feesStartY, (label.feesLabel || '').toUpperCase());
        doc.line(425, feesStartY + 4, 19, feesStartY + 4);

        doc.setFontSize(14);
        let feeCurrentY = feesStartY + 22;

        if (provincePricingEnabled && selectedProvince) {
          setFontBasedOnLanguage(doc, languageCode, 'DIN2014');
          doc.text(19, feeCurrentY, label.yourProvinceLabel || 'Province');
          setFontBasedOnLanguage(doc, languageCode);
          doc.text(425, feeCurrentY, selectedProvince.name, 'right');
          feeCurrentY += 30;

          if (provincialFees) {
            // Show freight fee
            if (provincialFees.freightFee !== undefined && provincialFees.freightFee !== null) {
              const freightFeeObject = fees?.find(fee => fee.title?.toLowerCase().includes('freight'));
              const freightTitle = freightFeeObject?.title || label.freightFeesLabel || label.freightLabel;

              doc.text(19, feeCurrentY, freightTitle);
              if (freightFeeObject?.description) {
                const feeDescriptionWithoutHTMLTags = freightFeeObject.description
                  ? freightFeeObject.description.replace(/<[^>]+>/gi, '')
                  : '';
                const feeDescriptionWithoutHTMLEntities = feeDescriptionWithoutHTMLTags
                  .replace(/&#8217;/gi, "'")
                  .replace(/&#8211;/gi, '–')
                  .replace(/&nbsp;/gi, ' ')
                  .replace(/&quot;/gi, '"')
                  .replace(/&amp;/gi, '&');
                const formattedFeeDescription = `${feeDescriptionWithoutHTMLEntities}`;

                doc.setFontSize(12);
                doc.setTextColor('#585a5f');
                const splitDescription = doc.splitTextToSize(formattedFeeDescription, 420);
                doc.text(19, feeCurrentY + 20, splitDescription);
                doc.setTextColor('black');
                doc.setFontSize(14);
              }
              doc.text(425, feeCurrentY, formatPrice(provincialFees.freightFee || 0), 'right');
              feeCurrentY += freightFeeObject?.description ? 60 : 30;
            }

            if (provincialFees.dealerFee !== undefined && provincialFees.dealerFee !== null) {
              const dealerFeeObject = fees?.find(
                fee => fee.title?.toLowerCase().includes('dealer') || fee.title?.toLowerCase().includes('pdi')
              );
              const dealerTitle = dealerFeeObject?.title || label.dealerFeesLabel || label.pdiDealerFeesLabel;

              doc.text(19, feeCurrentY, dealerTitle);
              if (dealerFeeObject?.description) {
                const feeDescriptionWithoutHTMLTags = dealerFeeObject.description
                  ? dealerFeeObject.description.replace(/<[^>]+>/gi, '')
                  : '';
                const feeDescriptionWithoutHTMLEntities = feeDescriptionWithoutHTMLTags
                  .replace(/&#8217;/gi, "'")
                  .replace(/&#8211;/gi, '–')
                  .replace(/&nbsp;/gi, ' ')
                  .replace(/&quot;/gi, '"')
                  .replace(/&amp;/gi, '&');
                const formattedFeeDescription = `${feeDescriptionWithoutHTMLEntities}`;

                doc.setFontSize(12);
                doc.setTextColor('#585a5f');
                const splitDescription = doc.splitTextToSize(formattedFeeDescription, 420);
                doc.text(19, feeCurrentY + 20, splitDescription);
                doc.setTextColor('black');
                doc.setFontSize(14);
              }
              doc.text(425, feeCurrentY, formatPrice(provincialFees.dealerFee || 0), 'right');
              feeCurrentY += dealerFeeObject?.description ? 60 : 30;
            }
          }
        } else if (fees && fees.length > 0) {
          fees.forEach(fee => {
            const feeDescriptionWithoutHTMLTags = fee.description ? fee.description.replace(/<[^>]+>/gi, '') : '';
            const feeDescriptionWithoutHTMLEntities = feeDescriptionWithoutHTMLTags
              .replace(/&#8217;/gi, "'")
              .replace(/&#8211;/gi, '–')
              .replace(/&nbsp;/gi, ' ')
              .replace(/&quot;/gi, '"')
              .replace(/&amp;/gi, '&');
            const formattedFeeDescription = `${feeDescriptionWithoutHTMLEntities}`;

            doc.text(19, feeCurrentY, fee.title);
            doc.setFontSize(12);
            doc.setTextColor('#585a5f');
            const splitDescription = doc.splitTextToSize(formattedFeeDescription, 420);
            doc.text(19, feeCurrentY + 20, splitDescription);
            doc.setTextColor('black');
            doc.setFontSize(14);
            doc.text(425, feeCurrentY, fee.priceLocalised, 'right');

            feeCurrentY += 60;
          });
        }
        totalY = feeCurrentY;
      } else {
        // No fees section - calculate total position
        const didAddPage = didAddPageBreak;
        if (hasTotalCare && doc.lastAutoTable) {
          const spacing = (bikeAccessoriesWithImages.length || packs) && packAndAccessoriesTotal > 0 ? 70 : 40;
          totalY = doc.lastAutoTable.finalY + spacing;
        } else if (didAddPage) {
          totalY = 80;
        } else if (doc.lastAutoTable) {
          totalY = doc.lastAutoTable.finalY + 40;
        } else {
          totalY = currentY + 40;
        }
      }
    })();

    doc.setFontSize(16);
    doc.setTextColor('black');
    setFontBasedOnLanguage(doc, languageCode, 'DIN2014');

    let totalCarePrice = 0;
    if (selectedWarrantyPack && selectedWarrantyPack.price) {
      const price =
        typeof selectedWarrantyPack.price === 'string'
          ? parseFloat(selectedWarrantyPack.price.replace(/[^0-9.-]/g, ''))
          : Number(selectedWarrantyPack.price);
      totalCarePrice += price || 0;
    }
    if (selectedServicePack && selectedServicePack.price) {
      const price =
        typeof selectedServicePack.price === 'string'
          ? parseFloat(selectedServicePack.price.replace(/[^0-9.-]/g, ''))
          : Number(selectedServicePack.price);
      totalCarePrice += price || 0;
    }

    const totalPrice =
      bikeAccessoriesWithImages.length || packs
        ? packAndAccessoriesTotal + basicPrice + totalCarePrice
        : basicPrice + totalCarePrice;

    if (label.totalSellingPriceLabel) {
      doc.text(425, totalY + 30, label.totalSellingPriceLabel || '', 'right');
    }
    doc.text(425, totalY + 45, formatPrice(totalPrice) || '0', 'right');

    doc.setFontSize(10);
    doc.setTextColor('#585a5f');
    setFontBasedOnLanguage(doc, languageCode, 'DIN2014Regular');

    // Calculate disclaimer position and check if it fits
    const disclaimerStartY = totalY + 80;
    let disclaimerText = '';

    if (!luxuryTax) {
      disclaimerText = `${formatPriceDisclaimer}\n\n${formatFinanceDisclaimer}\n\n${formatPDFDisclaimer}`;
      if (packs) {
        disclaimerText += `\n\n${formatPacksDisclaimer}`;
      }
    } else {
      disclaimerText = `${formatPriceDisclaimer}\n\n${formatFinanceDisclaimer}\n\n${formatBasicPriceDisclaimer}\n\n${formatAccessoriesPriceDisclaimer}\n\n${formatPDFDisclaimer}`;
      if (packs) {
        disclaimerText += `\n\n${formatPacksDisclaimer}`;
      }
    }

    const splitDisclaimer = doc.splitTextToSize(disclaimerText, 406);
    // Calculate actual disclaimer height (10px font size + line spacing)
    const lineHeight = 12; // Font size 10 + 2px line spacing
    const disclaimerHeight = splitDisclaimer.length * lineHeight;

    // Check if disclaimer will overflow the page
    if (disclaimerStartY + disclaimerHeight > PAGE_HEIGHT - BOTTOM_MARGIN) {
      // Move to next page if disclaimer would overflow
      doc.addPage();
      // Place disclaimer at top of new page
      doc.text(19, TOP_MARGIN, splitDisclaimer);
    } else {
      // Place disclaimer on current page
      doc.text(19, disclaimerStartY, splitDisclaimer);
    }

    if (isIE() || isEdge()) {
      doc.save(`bike-summary.pdf`);
    } else {
      window.open(doc.output('bloburl', { filename: `${label.bikeSummaryLabel || 'summary'}.pdf` }));
    }
  };

  return generatePDF;
};
